import React from 'react';

import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';

import { mainContainerStyles, sidebarStyles } from './styles';

export const getChildrenTypes = (
  children: React.ReactElement | React.ReactElement[],
  types: React.FC[],
) => {
  return types.map((type) => {
    if (!Array.isArray(children)) {
      return children.type === type ? children : null;
    }

    return children.find((child) => child.type === type) ?? null;
  });
};

const Layout: React.FC<{
  children: React.ReactElement | React.ReactElement[];
  showLeft?: boolean;
  showTop?: boolean;
  dir?: string;
  lang?: string;
}> & {
  Top: React.FC<React.PropsWithChildren>;
  Left: React.FC<React.PropsWithChildren>;
  Main: React.FC<React.PropsWithChildren>;
} = ({ children, showLeft = true, showTop = true, dir, lang }) => {
  const [Top, Left, Main] = getChildrenTypes(children, [Layout.Top, Layout.Left, Layout.Main]);

  return (
    <Stack className="relative h-screen overflow-hidden" direction="col">
      {showTop && Top ? (
        <Box
          className="z-20 h-56 w-full border-b border-divider bg-bg-primary"
          dir={dir}
          lang={lang}>
          {Top}
        </Box>
      ) : null}
      <Stack className="flex-1 overflow-hidden">
        {Left ? (
          <Box
            className={cn(
              'flex-shrink-0 transform bg-bg-secondary transition-all',
              showLeft ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full',
              sidebarStyles,
            )}
            dir={dir}
            lang={lang}>
            {Left}
          </Box>
        ) : null}
        <Box
          className={cn('flex-1 transition-all', mainContainerStyles(showLeft, Left))}
          dir={dir}
          lang={lang}>
          {Main}
        </Box>
      </Stack>
    </Stack>
  );
};

Layout.Top = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
Layout.Top.displayName = 'Layout Top';

Layout.Left = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
Layout.Left.displayName = 'Layout Left';

Layout.Main = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
Layout.Main.displayName = 'Layout Main';

export default Layout;
